import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProgressBars from '../../components/customLayout/progressBars';
import ListTab from './ListTab';
import BankTab from './BankTab';
import TeamTab from './TeamTab';
import PreviewTab from './PreviewTab';
import FinalTab from './FinalTab';
import { useDialogStore } from '../../store/dialogStore';
import { AlertTriangleIcon, TrophyIcon } from 'lucide-react';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import { useClubStore } from '../../store/clubStore';
import axiosInstance from '../../axiosConfig';
import { useAssociationStore } from '../../store/associationStore';
import { useReportStore } from '../../store/reportStore';
import type { ReportData, ReportListItem } from '../../store/reportStore';
import PdfInfoBanner from './components/PdfInfoBanner';
import { TeamRegistrationService } from './TeamRegistrationService';
import MissingFieldsNotice from './components/MissingFieldsNotice';

type TabKey = 'list' | 'bank' | 'team' | 'preview' | 'final';
const VALID_TABS: TabKey[] = ['list', 'bank', 'team', 'preview', 'final'];
const TAB_ORDER: TabKey[] = ['bank', 'team', 'preview', 'final'];
const BASE_ROUTE = '/team-registration';
const REQUIRED_IBAN_LENGTH = 22; // DE IBAN length

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const TAB_ITEMS = [
  { key: 'bank', title: 'Bankkonto', description: 'Bankdaten', icon: '🏦' },
  { key: 'team', title: 'Team', description: 'Team wählen', icon: '👥' },
  { key: 'preview', title: 'Vorschau', description: 'Überprüfung', icon: '👀' },
  { key: 'final', title: 'Abschluss', description: 'Bestätigung', icon: '✅' },
];

const TeamRegistrationLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getCurrentId = () => {
    const pathSegments = location.pathname.split('/');
    if (pathSegments.length >= 3 && pathSegments[1] === 'team-registration') {
      return pathSegments[2];
    }
    return null;
  };

  const iban = useTeamRegistrationStore((state) => state.iban);
  const teams = useTeamRegistrationStore((state) => state.teams);
  const reporterName = useTeamRegistrationStore((state) => state.reporterName);
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const myClub = useClubStore((state) => state.myClub);
  const setAssociationStore = useAssociationStore(
    (state) => state.setAssociation,
  );
  const association = useAssociationStore((state) => state.association);
  const setReportStore = useReportStore((state) => state.setReport);
  const report = useReportStore((state) => state.report);
  const setReportId = useTeamRegistrationStore((state) => state.setReportId);
  const setInvoiceDate = useTeamRegistrationStore(
    (state) => state.setInvoiceDate,
  );
  const finalData = useTeamRegistrationStore((state) => state.finalData);
  const setReporterName = useTeamRegistrationStore(
    (state) => state.setReporterName,
  );
  const setTeams = useTeamRegistrationStore((state) => state.setTeams);
  const setEditId = useTeamRegistrationStore((state) => state.setEditId);
  const editId = useTeamRegistrationStore((state) => state.editId);
  const setPdfUrl = useTeamRegistrationStore((state) => state.setPdfUrl);
  const title = useReportStore((state) => state.report?.title);
  const [isInitLoading, setIsInitLoading] = useState(true);

  useEffect(() => {
    if (!myClub) {
      setIsInitLoading(true);
      return;
    }
    setIsInitLoading(true);
    let isMounted = true;
    const fetchAssociationInfo = async () => {
      try {
        const response = await axiosInstance.get('association/info');
        setAssociationStore(response.data);
      } catch (error) {
        console.error('association/info fetch failed:', error);
      } finally {
        if (isMounted) setIsInitLoading(false);
      }
    };
    fetchAssociationInfo();
    return () => {
      isMounted = false;
    };
  }, [myClub]);

  const isMainPage = location.pathname === BASE_ROUTE;

  const fetchInitialReport = async () => {
    const currentId = getCurrentId();
    if (
      location.pathname.startsWith(BASE_ROUTE) &&
      location.pathname !== BASE_ROUTE &&
      currentId
    ) {
      try {
        const res = await axiosInstance.get(
          `report/${currentId}?timestamp=${Date.now()}`,
        );
        setReportStore(res.data);
        setReportId(Number(currentId));
        setInvoiceDate(res.data.invoiceDate);
        fetchReportResult(res.data);
      } catch (err) {
        console.error('report fetch failed:', err);
      }
    }
  };

  const fetchReportResult = async (reportData: ReportData) => {
    const currentId = getCurrentId();
    if (
      location.pathname.startsWith(BASE_ROUTE) &&
      location.pathname !== BASE_ROUTE &&
      currentId
    ) {
      try {
        const res = await axiosInstance.get(
          `report/result/${currentId}?timestamp=${Date.now()}`,
        );
        if (res.status === 200) {
          const list: ReportListItem[] = reportData.list || [];
          // Klasse number => stored Spielgemeinschaft club name
          const spielgemeinschaft: Record<string, string> =
            res.data?.['spielgemeinschaft'] || {};
          const mappedTeams = list.map(
            (item: ReportListItem, index: number) => {
              const klasseIndex = index + 1;
              const rawValue = res.data?.[`klasse${klasseIndex}`];
              const selectedTeams = rawValue == null ? 0 : Number(rawValue);
              return {
                ...item,
                id: index,
                selectedTeams,
                klasse: klasseIndex,
                spielgemeinschaftClub:
                  spielgemeinschaft[String(klasseIndex)] || undefined,
              };
            },
          );

          setEditId(res.data['id']);
          setPdfUrl(res.data['pdf_url']);
          setTeams(mappedTeams);
          setReporterName(res.data['reporter_name']);
        }
      } catch (err) {
        console.error('report fetch failed:', err);
      }
    }
  };

  async function fetchAll() {
    await fetchInitialReport();
  }

  useEffect(() => {
    fetchAll();
    // run only once on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentId()]);

  // Centralized guard to prevent leaving Bank tab without IBAN
  const warnBankInfoMissingAndBlock = useCallback(
    (fromKey: string, toKey: string): boolean => {
      if (fromKey !== 'bank' || toKey === 'bank') return false;
      const hasEnoughIban = (iban || '').trim().length >= REQUIRED_IBAN_LENGTH;
      if (hasEnoughIban) return false;
      openDialog(
        <div className="py-5 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangleIcon className="w-8 h-8 text-amber-600" />
          </div>
          <div className="text-lg font-semibold text-gray-900 mb-2">
            Bankdaten erforderlich
          </div>
          <div className="text-sm text-gray-600">
            Bitte geben Sie zuerst Ihre Bankdaten (IBAN) im Schritt{' '}
            <span className="font-semibold text-[#52A445]">Bankkonto</span> ein.
          </div>
        </div>,
        () => closeDialog(),
        true,
      );
      return true;
    },
    [iban, openDialog, closeDialog],
  );

  // Guard: From Team tab, ensure no unlocked item has null selection (0 is allowed) and reporter is provided
  const warnTeamOrReporterMissingAndBlock = useCallback(
    (fromKey: string, toKey: string): boolean => {
      if (fromKey !== 'team' || toKey === 'team') return false;
      if (toKey !== 'preview' && toKey !== 'final') return false; // only block forward
      const hasReporter = (reporterName || '').trim().length > 0;
      const list = report?.list || [];
      // Check if any unlocked item has selection == null (0 is allowed)
      const hasNullSelection = list.some((item, index) => {
        if (item.lock) return false;
        const team = teams.find((t) => t.id === index);
        return (team?.selectedTeams ?? null) === null;
      });
      const missingSpielgemeinschaftClubs =
        TeamRegistrationService.missingSpielgemeinschaftClubs(
          association,
          teams,
        );

      if (
        !hasNullSelection &&
        hasReporter &&
        missingSpielgemeinschaftClubs.length === 0
      )
        return false;

      openDialog(
        <MissingFieldsNotice
          needsTeamSelection={hasNullSelection}
          needsReporter={!hasReporter}
          missingSpielgemeinschaftClubs={missingSpielgemeinschaftClubs}
        />,
        () => closeDialog(),
        true,
      );
      return true;
    },
    [teams, reporterName, report, association, openDialog, closeDialog],
  );

  const handleTabChange = useCallback(
    (key: string) => {
      const currentActiveTab = getActiveTab();
      if (currentActiveTab === 'final' || key === 'final') return;

      if (warnBankInfoMissingAndBlock(currentActiveTab, key)) return;
      if (warnTeamOrReporterMissingAndBlock(currentActiveTab, key)) return;

      const currentId = getCurrentId();
      const targetPath = currentId
        ? `${BASE_ROUTE}/${currentId}/${key}`
        : BASE_ROUTE;

      navigate(targetPath);
      scrollToTop();
    },
    [warnBankInfoMissingAndBlock, warnTeamOrReporterMissingAndBlock, navigate],
  );

  const getActiveTab = (): TabKey => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return (VALID_TABS as string[]).includes(lastSegment)
      ? (lastSegment as TabKey)
      : 'list';
  };

  const getActiveTabFromPath = (pathname: string): TabKey => {
    const pathSegments = pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return (VALID_TABS as string[]).includes(lastSegment)
      ? (lastSegment as TabKey)
      : 'list';
  };

  const activeTab = getActiveTab();

  const handleNextTab = useCallback(() => {
    const currentActiveTab = getActiveTab();
    const currentId = getCurrentId();
    const currentIndex = TAB_ORDER.indexOf(currentActiveTab);

    if (currentIndex < TAB_ORDER.length - 1) {
      const nextTab = TAB_ORDER[currentIndex + 1];

      if (warnBankInfoMissingAndBlock(currentActiveTab, nextTab)) return;
      if (warnTeamOrReporterMissingAndBlock(currentActiveTab, nextTab)) return;
      if (currentId) {
        navigate(`${BASE_ROUTE}/${currentId}/${nextTab}`);
        scrollToTop();
      }
    }
  }, [
    warnBankInfoMissingAndBlock,
    warnTeamOrReporterMissingAndBlock,
    navigate,
  ]);

  const handlePreviousTab = useCallback(() => {
    const currentActiveTab = getActiveTab();
    const currentId = getCurrentId();
    const currentIndex = TAB_ORDER.indexOf(currentActiveTab);

    if (currentIndex > 0) {
      const previousTab = TAB_ORDER[currentIndex - 1];
      if (currentId) {
        navigate(`${BASE_ROUTE}/${currentId}/${previousTab}`);
        scrollToTop();
      }
    }
  }, [navigate]);

  // Track previous path to detect leaving the final tab
  const previousPathRef = useRef(location.pathname);
  useEffect(() => {
    const previousActiveTab = getActiveTabFromPath(previousPathRef.current);
    const currentActiveTab = getActiveTabFromPath(location.pathname);

    if (previousActiveTab === 'final' && currentActiveTab !== 'final') {
      // Clear final data when leaving final tab and redirect to base page
      useTeamRegistrationStore.setState({ finalData: null });
      if (location.pathname !== BASE_ROUTE) {
        navigate(BASE_ROUTE);
      }
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, navigate]);

  // Auto-redirect to bank tab if IBAN not provided or too short and user is on any other tab
  useEffect(() => {
    const hasEnoughIban = (iban || '').trim().length >= REQUIRED_IBAN_LENGTH;
    if (activeTab !== 'bank' && !isMainPage && !hasEnoughIban) {
      const currentId = getCurrentId();
      if (currentId) {
        navigate(`${BASE_ROUTE}/${currentId}/bank`);
        scrollToTop();
      }
    }
  }, [activeTab, iban, isMainPage, location.pathname]);

  // If not on tab pages, clear editId so the PDF banner disappears
  useEffect(() => {
    const segments = location.pathname.split('/');
    const hasId = segments.length >= 3 && !!segments[2];
    const lastSegment = segments[segments.length - 1];
    const onTabPage = hasId && (VALID_TABS as string[]).includes(lastSegment);
    if (!onTabPage) {
      setEditId(null);
    }
  }, [location.pathname, setEditId]);

  // Guard: If user is on final tab without a valid transaction_id, redirect to base
  useEffect(() => {
    if (activeTab === 'final') {
      if (!finalData || finalData.transaction_id == null) {
        if (location.pathname !== BASE_ROUTE) {
          navigate(BASE_ROUTE);
        }
      }
    }
  }, [activeTab, finalData, navigate, location.pathname]);

  const renderTabContent = () => {
    const tabComponents: Record<string, React.ReactElement> = {
      bank: (
        <BankTab
          onNext={handleNextTab}
          onPrevious={handlePreviousTab}
          showNextButton={true}
          showPreviousButton={false}
        />
      ),
      team: (
        <TeamTab
          onNext={handleNextTab}
          onPrevious={handlePreviousTab}
          showNextButton={true}
          showPreviousButton={true}
        />
      ),
      preview: (
        <PreviewTab
          onNext={handleNextTab}
          onPrevious={handlePreviousTab}
          showNextButton={true}
          showPreviousButton={true}
        />
      ),
      final: (
        <FinalTab
          onNext={handleNextTab}
          onPrevious={handlePreviousTab}
          showNextButton={false}
          showPreviousButton={false}
        />
      ),
    };

    return tabComponents[activeTab] || <ListTab />;
  };

  return (
    <div className="px-5 grid gap-4 pb-20">
      {isInitLoading ? (
        <div className="bg-white border rounded-md p-6">
          <div className="h-48 flex flex-col items-center justify-center text-gray-600 text-sm gap-3">
            <div
              className="h-12 w-12 border-4 border-gray-300 border-t-[#52A445] rounded-full animate-spin"
              aria-label="Laden"
            />
            <div>Bitte warten Sie. Wir bereiten die Registrierung vor...</div>
          </div>
        </div>
      ) : (
        <>
          {!isMainPage && (
            <>
              {title && (
                <div className="font-bold flex items-center gap-2 text-xl">
                  <TrophyIcon className="w-5 h-5" />
                  {title}
                </div>
              )}
              <ProgressBars
                items={TAB_ITEMS}
                activeItemKey={activeTab}
                onItemClick={handleTabChange}
              />
            </>
          )}

          {editId && !isMainPage && activeTab === 'bank' && <PdfInfoBanner />}
          <div className="bg-white border rounded-md p-4">
            {renderTabContent()}
          </div>
        </>
      )}
    </div>
  );
};

export default TeamRegistrationLayout;
